import React, { JSX } from 'react';
import { styled } from '@mui/material/styles';
import { IClientMessage } from 'src/interfaces';
import { Sender } from './sender/Sender';
import { type SenderSource } from '@enuan/chat__utils';

/**
 * MessageHeader props.
 * @typedef {Object} IMessageHeaderProps
 * @property {React.ReactNode} [customHeader] - Replaces the default header (sender name)
 * @property {boolean} [hideSender] - When true, hides the sender name
 * @property {SenderSource} [sender] - Sender source (agent/system/user) for the label
 */
export type IMessageHeaderProps = Omit<
	JSX.IntrinsicElements['header'],
	'children'
> & {
	customHeader?: React.ReactNode;
	hideSender?: boolean;
	sender?: IClientMessage['sender'];
};

/**
 * Message header: shows the sender name (Agent/System/User) or custom header content.
 *
 * **Behaviour:**
 * - If `customHeader` is provided, it is rendered instead of the default content.
 * - Otherwise, when `hideSender` is false and `sender` is set, the Sender component is shown,
 *   displaying the localized label (e.g. "Agent", "System", "User") based on `SenderSource`.
 * - Positioned in the Message grid via gridArea: 'header'.
 *
 * @param {IMessageHeaderProps} props - customHeader, hideSender, sender and header HTML attributes
 * @returns {JSX.Element} Header element with sender name or custom content
 */
export const MessageHeader = styled(
	({
		customHeader,
		hideSender = false,
		sender,
		...props
	}: IMessageHeaderProps) => (
		<header {...props}>
			{customHeader ?? (
				<MessageHeaderContent sender={sender} hideSender={hideSender} />
			)}
		</header>
	),
	{ label: 'MessageHeader', target: 'MessageHeader-root' },
)({
	gridArea: 'header',
	display: 'flex',
	fontFamily: 'Ubuntu, sans-serif',
	fontWeight: 600,
	fontSize: '1.2em',
	color: 'var(--message__sender_color)',
	alignItems: 'flex-end',
});

type IMessageHeaderContentProps = {
	sender?: SenderSource;
	hideSender?: boolean;
};
const MessageHeaderContent = ({
	sender,
	hideSender,
}: IMessageHeaderContentProps) => {
	if (hideSender || !sender) return null;
	return <Sender sender={sender} />;
};
